import { ErhlLoanPurpose } from '@/helpers/const';
import type { ErhlCustomerLoanAppMachineContext } from '@/helpers/loanErhlCustomerAppMachine';
import { ErhlCustomerApplicationProcessSteps, ErhlCustomerProcessStep, StepStatus } from '@/types';
import type { LoanApplication } from '@/types';

/** A D2C purchase application must list at least one existing (supporting) property */
export const isMissingExistingProperty = (
  loanApp: Pick<LoanApplication, 'erhlLoanPurposes' | 'supportingProperties'>,
): boolean =>
  (loanApp.erhlLoanPurposes ?? []).includes(ErhlLoanPurpose.PURCHASE_NEW_PROPERTY) &&
  (loanApp.supportingProperties ?? []).length === 0;

/**
 * Sends a resumed journey back to Property details when a purchase application saved before the
 * existing property became required has none. Submitted journeys are left alone.
 */
export const reopenPropertyDetailsIfMissingExisting = (
  context: ErhlCustomerLoanAppMachineContext,
): ErhlCustomerLoanAppMachineContext => {
  const step = ErhlCustomerProcessStep.PropertyDetails;
  if (
    context.current === ErhlCustomerProcessStep.Finished ||
    context.status[step] !== StepStatus.Completed ||
    !isMissingExistingProperty(context.loanApp)
  ) {
    return context;
  }

  return { ...context, current: step, status: { ...context.status, [step]: StepStatus.Ready } };
};

/**
 * Where "Edit funds required" on Loan details goes: straight into the purchase property's edit form
 * when there's only one, otherwise the Property details page.
 */
export const getFundsRequiredEditRoute = (loanApp: Pick<LoanApplication, 'incomingProperties'>) => ({
  name: ErhlCustomerApplicationProcessSteps[ErhlCustomerProcessStep.PropertyDetails].route,
  query: loanApp.incomingProperties.length === 1 ? { edit: 'purchase' } : undefined,
});
